import { computed, type Ref } from 'vue';

import type { RegisterAPI } from '~/services/register-api/register-api.types';
import type { PartnerUser } from '~/store/coreUsers/usePartnerUsers';

type Associate = RegisterAPI.Associate;

/**
 * Returns users that are not already bound to any associate,
 * plus the user currently bound to the associate being edited.
 *
 * @param users - Full list of partner users
 * @param associates - Full list of associates
 * @param currentAssociateCpf - CPF of the associate currently being edited (null if none)
 */
export default function useAvailableUsers(
  users: Ref<PartnerUser[]>,
  associates: Ref<Associate[]>,
  currentAssociateCpf: Ref<null | string>,
) {
  const boundUserIds = computed(() => {
    const currentAssociate = associates.value.find(
      (a) => a.cpf === currentAssociateCpf.value,
    );
    const currentUserId = currentAssociate?.user_id ?? null;

    return new Set(
      associates.value
        .filter((a) => a.user_id !== null && a.user_id !== currentUserId)
        .map((a) => a.user_id),
    );
  });

  const availableUsers = computed(() => (
    users.value.filter((u) => !boundUserIds.value.has(u.id))
  ));

  return { availableUsers };
}
